/**
 * Unified application configuration
 * Single source of truth for all config values
 */

import { trimTrailingSlash, normalizeDomain } from "../lib/url";
import type { AppConfig, CognitoConfig, CognitoEndpoints } from "./types";

export type { AppConfig, CognitoConfig, CognitoEndpoints } from "./types";

// Environment detection
const isLocal = typeof window !== "undefined" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

// Runtime config from window.__APP_CONFIG__
const windowConfig = (typeof window !== "undefined" && window.__APP_CONFIG__) || {};

/**
 * Get a config value with fallback to env variable
 */
function pick(key: keyof AppConfig, envKey: string, fallback = ""): string {
  const winVal = (windowConfig[key] as string | undefined) ?? "";
  const envVal = (typeof process !== "undefined" && process.env?.[envKey]) ?? "";
  return (winVal || envVal || fallback).trim();
}

/**
 * Require a config value - throws in production, warns in dev
 */
function require(value: string, name: string): string {
  if (!value) {
    if (isLocal) {
      console.warn(`[config] Missing: ${name} (allowed in dev)`);
      return value;
    }
    throw new Error(`Missing required config: ${name}`);
  }
  return value;
}

// Load raw values
const rawDomain = pick("domain", "REACT_APP_DOMAIN");
const rawClientId = pick("clientId", "REACT_APP_CLIENT_ID");
const rawUserPoolId = pick("userPoolId", "REACT_APP_USER_POOL_ID");
const rawRegion = pick("region", "REACT_APP_REGION");
const rawApiBase = pick("apiBase", "REACT_APP_API_BASE");
const rawWsBase = pick("wsBase", "REACT_APP_WS_BASE");
const rawScope = pick("scope", "REACT_APP_SCOPE", "openid email profile");
const rawOuraClientId = pick("ouraClientId", "REACT_APP_OURA_CLIENT_ID");

// Cached config instance
let _config: Readonly<AppConfig> | null = null;

/**
 * Get the full application config
 */
export function getConfig(): Readonly<AppConfig> {
  if (_config) return _config;

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  _config = Object.freeze({
    domain: normalizeDomain(require(rawDomain, "domain")),
    clientId: require(rawClientId, "clientId"),
    userPoolId: require(rawUserPoolId, "userPoolId"),
    region: require(rawRegion, "region"),
    scope: rawScope,
    apiBase: trimTrailingSlash(require(rawApiBase, "apiBase")),
    wsBase: trimTrailingSlash(rawWsBase || ""),
    redirectUri: (windowConfig.redirectUri as string) || `${origin}/_auth/callback`,
    logoutRedirectUri: (windowConfig.logoutRedirectUri as string) || `${origin}/`,
    ouraClientId: rawOuraClientId,
  });

  return _config;
}

// Initialize config
const config = getConfig();

// ============================================
// Named exports for backward compatibility
// ============================================

// API Endpoints
export const API_BASE = config.apiBase;
export const WS_BASE = config.wsBase;

// Cognito
export const REGION = config.region;
export const COGNITO_DOMAIN = config.domain;
export const COGNITO_CLIENT_ID = config.clientId;
export const OIDC_SCOPE = config.scope;

// Oura
export const OURA_CLIENT_ID = config.ouraClientId;

// OAuth URLs
export const HOSTED_UI_BASE = `https://${config.domain}`;
export const AUTHORIZE_URL = `${HOSTED_UI_BASE}/oauth2/authorize`;
export const TOKEN_URL = `${HOSTED_UI_BASE}/oauth2/token`;
export const LOGOUT_URL = `${HOSTED_UI_BASE}/logout`;

// ============================================
// COGNITO object (for auth/client.ts compatibility)
// ============================================

export const COGNITO: CognitoConfig = {
  domain: config.domain,
  clientId: config.clientId,
  userPoolId: config.userPoolId,
  region: config.region,
  scope: config.scope,
  apiBase: config.apiBase,
  redirectUri: config.redirectUri,
  logoutRedirectUri: config.logoutRedirectUri,
};

export const COGNITO_ENDPOINTS: CognitoEndpoints = {
  authorize: AUTHORIZE_URL,
  token: TOKEN_URL,
  logout: LOGOUT_URL,
};
